import { useSelector } from 'react-redux'
import React, { useState } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import theme from '@/theme'
import PropTypes from 'prop-types'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconUser from '@assets/images/icons/userPlaceholder.svg'

import ClientInvitationsModal from '@/modules/client/components/modals/client-invitations-modal'
import SubscriptionDanglingModal from '@/modules/my-account/subscription-dangling-modal/subscription-dangling-modal'

import Info from '@/modules/layout/components/menu/header/info'
import TenantInitialsBubble from '@/modules/my-account/tenant-information/tenant-initials-bubble'
import ConfirmEmailModal from '@/modules/account/components/confirm-email/confirm-email-modal'
import { useNavigate } from 'react-router'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    marginBottom: 30,
  },
  avatar: {
    border: `2px solid ${theme.borderColor}`,
    borderRadius: '50%',
    width: 82,
    height: 82,
    display: 'flex',
    cursor: 'pointer',
    '& img': {
      borderRadius: '50%',
      width: '100%',
    },
    '&&:hover': {
      border: `2px solid ${theme.primaryDark}`,
    },
  },
  avatarIcon: {
    width: 80,
    height: 80,
  },
  tenantBubble: {
    height: 50,
    minWidth: 50,
    maxWidth: 50,
    fontSize: theme.fontSizes.xlarge,
  },
  greeting: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: 16,
    color: theme.textColor,
    lineHeight: '30px',
    fontSize: theme.fontSizes.large,
    fontWeight: theme.fontWeights.medium,
  },
  header: {
    maxWidth: 330,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  email: {
    fontSize: theme.fontSizes.default,
    fontWeight: theme.fontWeights.regular,
    maxWidth: 330,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  [theme.devices.mobile]: {
    avatar: {
      height: 44,
      width: 44,
    },
    avatarIcon: {
      width: '100%',
      height: '100%',
    },
    greeting: {
      fontSize: theme.fontSizes.default,
      lineHeight: 1.7,
    },
    email: {
      fontSize: theme.fontSizes.smaller,
      fontWeight: theme.fontWeights.regular,
    },
  },
}),
)

const UserHeader = ({ customClass, children, isTaxConsultant, hasInvites, useNewTenantAvatar = false }) => {
  const navigate = useNavigate()
  const classes = useStyles()

  const [openModal, setOpenModal] = useState(false)
  const [invitationsModalOpen, setInvitationsModalOpen] = useState(false)

  const {
    firstName,
    lastName,
    email,
    avatarImageUrl,
    hasConfirmedEmail,
    activeTenant: { companyName } } = useSelector(state => state.user.info)
  const { hasDanglingSubscriptions } = useSelector(state => state.subscription.details)
  const { subscriptions } = useSelector(state => state.tenant.info)
  const subscriptionTypeTypesLabel = subscriptions?.map(s => s.typeOfSubscription).join(', ') || ''

  const name = [firstName, lastName].join(' ').trim()
  const header = name
    ? `Hallo, ${firstName} ${lastName}!`
    : 'Hallo, Benutzer!'

  const handleTaxConsultantInvitationInfoClick = () => {
    navigate('/users')
  }

  const handleInvitationsModalClose = () => {
    setInvitationsModalOpen(false)
  }

  const handleEmailInfoClick = () => {
    setOpenModal(true)
  }
  const handleAvatarClick = () => {
    navigate('/my-account')
  }

  const hasTaxConsultantInvitation = !isTaxConsultant && hasInvites

  const avatar = useNewTenantAvatar
    ? (
      <TenantInitialsBubble
        tenantName={name}
        companyName={companyName}
        avatarUrl={avatarImageUrl}
        styles={{ root: classes.tenantBubble }}
        iconSize={50}
        onClick={handleAvatarClick}
      />
    )
    : (
      <div className={clsx(classes.avatar, customClass.avatar)} onClick={handleAvatarClick}>
        {!avatarImageUrl
          ? <SVGIcon svg={IconUser} fill={theme.primary} className={clsx(classes.avatarIcon, customClass.avatarIcon)} />
          : <img src={avatarImageUrl} />
        }
      </div>
    )

  return (
    <div className={clsx(classes.container, customClass.header)}>
      {avatar}
      <div className={clsx(classes.greeting, customClass.greeting)}>
        <span className={classes.header}>{header}</span>
        <Info onClick={handleEmailInfoClick}
          customClass={customClass}
          text={hasDanglingSubscriptions ? lang.myAccount.danglingSubscription.assignOpenSubscriptions : lang.userHeader.confirmEmail}
          shouldRender={!hasConfirmedEmail || hasDanglingSubscriptions}>
          <span className={clsx(classes.email, customClass.email)}>{email}</span>
        </Info>
        <Info
          onClick={handleTaxConsultantInvitationInfoClick}
          customClass={customClass}
          text={lang.userHeader.confirmTaxConsultant}
          shouldRender={hasTaxConsultantInvitation} />
        {openModal && !hasDanglingSubscriptions && <ConfirmEmailModal
          closeModal={() => setOpenModal(false)}
          open={openModal}
          isModal
          gaData={{
            'eventCategory': 'DOI',
            'eventAction': 'Userbubble',
            'eventLabel': `DOI durchgeführt ${subscriptionTypeTypesLabel}`,
          }}
        />}
        {openModal && hasDanglingSubscriptions && <SubscriptionDanglingModal open={openModal} onClose={() => setOpenModal(false)} key={'subscription-dangling'} />}
        {invitationsModalOpen && <ClientInvitationsModal isOpen={invitationsModalOpen} onClose={handleInvitationsModalClose} />}
      </div>
      {children}
    </div>
  )
}

UserHeader.propTypes = {
  customClass: PropTypes.object,
  children: PropTypes.node,
  taxConsultant: PropTypes.object,
}

UserHeader.defaultProps = {
  customClass: {},
  taxConsultant: {},
}

export default UserHeader
